import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HelpCircle, X, Search, BookOpen, ChevronRight, ExternalLink, ListChecks,
} from 'lucide-react';
import {
  getContextSection,
  searchTutorial,
  QUICK_WORKFLOWS,
  TUTORIAL_SECTIONS,
} from '../data/platformTutorial';

export default function PlatformHelpWidget() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const contextSection = useMemo(
    () => getContextSection(location.pathname),
    [location.pathname],
  );

  const searchResults = useMemo(() => {
    if (!query.trim()) return [];
    return searchTutorial(query).slice(0, 6);
  }, [query]);

  const hideOnPaths = ['/', '/login', '/auth', '/reset-password'];
  const isWhiteLabelAuth = /^\/w\/[^/]+\/auth$/.test(location.pathname);
  const isPublicAuth = hideOnPaths.includes(location.pathname) || isWhiteLabelAuth;
  if (isPublicAuth || !localStorage.getItem('token')) return null;
  if (location.pathname.startsWith('/platform-guide')) return null;

  const openGuide = (sectionId) => {
    setOpen(false);
    navigate(sectionId ? `/platform-guide?section=${sectionId}` : '/platform-guide');
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-6 z-[100] flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/30 hover:scale-105 transition-transform"
        title="Platform help"
        aria-label="Open platform help"
        data-testid="platform-help-fab"
      >
        <HelpCircle size={22} />
      </button>

      {open && (
        <div className="fixed inset-0 z-[101] flex items-end sm:items-center justify-center sm:p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/70"
            onClick={() => setOpen(false)}
            aria-label="Close help"
          />
          <div className="relative w-full max-w-md max-h-[85vh] overflow-hidden rounded-t-2xl sm:rounded-2xl border border-white/10 bg-[#0d0d14] shadow-2xl flex flex-col">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div className="flex items-center gap-2">
                <HelpCircle size={18} className="text-violet-400" />
                <span className="font-semibold text-sm text-white">Platform Help</span>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white p-1">
                <X size={18} />
              </button>
            </div>

            <div className="p-4 border-b border-white/5">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search help…"
                  className="w-full rounded-lg border border-white/10 bg-black/40 py-2 pl-9 pr-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-violet-500/40"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {query ? (
                searchResults.length > 0 ? (
                  searchResults.map((sec) => (
                    <button
                      key={sec.id}
                      type="button"
                      onClick={() => openGuide(sec.id)}
                      className="w-full text-left rounded-lg border border-white/10 p-3 hover:bg-white/[0.04]"
                    >
                      <p className="text-sm font-medium text-white">{sec.title}</p>
                      <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{sec.summary}</p>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-zinc-500 text-center py-4">No results — try &quot;email&quot;, &quot;domain&quot;, or &quot;plan&quot;.</p>
                )
              ) : (
                <>
                  {contextSection && (
                    <div className="rounded-xl border border-violet-500/30 bg-violet-500/10 p-4">
                      <p className="text-[10px] uppercase tracking-wider text-violet-300 mb-1">This page</p>
                      <p className="font-semibold text-white text-sm">{contextSection.title}</p>
                      <p className="text-xs text-zinc-400 mt-1 line-clamp-3">{contextSection.summary}</p>
                      <button
                        type="button"
                        onClick={() => openGuide(contextSection.id)}
                        className="mt-3 text-xs text-violet-300 hover:text-violet-200 flex items-center gap-1"
                      >
                        Read full guide <ChevronRight size={12} />
                      </button>
                    </div>
                  )}

                  <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1">
                      <ListChecks size={12} /> Quick workflows
                    </p>
                    <div className="space-y-1">
                      {QUICK_WORKFLOWS.slice(0, 4).map((wf) => (
                        <button
                          key={wf.id}
                          type="button"
                          onClick={() => { setOpen(false); navigate(wf.path); }}
                          className="w-full flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-zinc-300 hover:bg-white/[0.04]"
                        >
                          <span>{wf.title}</span>
                          <ChevronRight size={14} className="text-zinc-600" />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">All modules</p>
                    <div className="grid grid-cols-2 gap-1">
                      {TUTORIAL_SECTIONS.filter((s) => s.path).slice(0, 10).map((sec) => (
                        <button
                          key={sec.id}
                          type="button"
                          onClick={() => openGuide(sec.id)}
                          className="rounded-lg px-2 py-1.5 text-left text-xs text-zinc-400 hover:text-white hover:bg-white/[0.04] truncate"
                        >
                          {sec.title}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="border-t border-white/10 p-4">
              <button
                type="button"
                onClick={() => openGuide()}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-violet-500 py-2.5 text-sm font-semibold text-white hover:bg-violet-400"
              >
                <BookOpen size={16} />
                Open full Platform Guide
                <ExternalLink size={14} className="opacity-70" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
